import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import UploadIcon from '@mui/icons-material/Upload';
import * as XLSX from 'xlsx';
import { useNotify, useRefresh } from "react-admin";
import { API } from "../providers/api.provider";
import { Alert, Box, CircularProgress, Link, Typography } from "@mui/material";
import { formatCPForCNPJ } from "../utils/mask.util";

/**
 * Formato esperado da planilha XLSX:
 * 
 * Colunas obrigatórias:
 * - nome: Nome completo do usuário
 * - email: Email principal
 * - usuario: Nome de usuário para login
 * - cpf_cnpj: CPF ou CNPJ (formatado - 99.999.999/9999-99 ou 999.999.999-99)
 * 
 * Colunas opcionais:
 * - telefone: Telefone principal
 * - emails_secundarios: Emails separados por vírgula ou ponto-e-vírgula
 * - telefones: Telefones separados por vírgula ou ponto-e-vírgula
 * - cep: CEP do endereço
 * - logradouro: Rua/Avenida
 * - numero: Número do endereço
 * - complemento: Complemento
 * - bairro: Bairro
 * - cidade: Cidade
 * - estado: Estado (sigla com 2 letras)
 * - empresa_id: ID da empresa (número)
 * - observacoes: Observações adicionais
 */

interface UserImportRow {
  nome: string;
  email: string;
  usuario: string;
  cpf_cnpj: string;
  telefone?: string;
  emails_secundarios?: string;
  telefones?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  empresa_id?: string | number;
  observacoes?: string;
}

export const ImportUsersButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const downloadTemplate = () => {
    // Criar planilha template
    const templateData = [
      {
        nome: "João da Silva",
        email: "joao@example.com",
        usuario: "joao.silva",
        cpf_cnpj: "123.456.789-01",
        telefone: "11987654321",
        emails_secundarios: "joao.secundario@example.com",
        telefones: "1133334444",
        cep: "01310100",
        logradouro: "Avenida Paulista",
        numero: "1000",
        complemento: "Apto 101",
        bairro: "Bela Vista",
        cidade: "São Paulo",
        estado: "SP",
        empresa_id: "",
        observacoes: "Usuário exemplo"
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Usuários");
    XLSX.writeFile(wb, "template_usuarios.xlsx");
  };

  const parseUserData = (row: UserImportRow) => {
    const userData: any = {
      name: row.nome,
      email: row.email,
      username: row.usuario,
      registration: formatCPForCNPJ(row.cpf_cnpj?.toString() || ''), // Formata CPF ou CNPJ
    };

    // Telefone principal
    if (row.telefone) {
      userData.phone = row.telefone.toString().replace(/\D/g, '');
    }

    // Emails secundários
    if (row.emails_secundarios) {
      const emailsArray = row.emails_secundarios
        .split(/[,;]/)
        .map(e => e.trim())
        .filter(e => e);
      if (emailsArray.length > 0) {
        userData.emails = emailsArray.map(email => ({ email }));
      }
    }

    // Telefones adicionais
    if (row.telefones) {
      const phonesArray = row.telefones
        .split(/[,;]/)
        .map(p => p.trim())
        .filter(p => p);
      if (phonesArray.length > 0) {
        userData.phones = phonesArray.map(phone => ({ phone: phone.replace(/\D/g, '') }));
      }
    }

    // Endereço
    if (row.cep || row.logradouro) {
      userData.address = {
        zipCode: row.cep?.toString().replace(/\D/g, ''),
        street: row.logradouro,
        number: row.numero,
        complement: row.complemento,
        district: row.bairro,
        city: row.cidade,
        state: row.estado,
      };
    }

    // Empresa
    if (row.empresa_id) {
      userData.company = { id: Number(row.empresa_id) };
    }

    // Observações
    if (row.observacoes) {
      userData.observations = row.observacoes;
    }

    return userData;
  };

  const handleImport = async () => {
    if (!file) {
      notify('Selecione um arquivo para importar', { type: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData: UserImportRow[] = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        notify('A planilha está vazia', { type: 'warning' });
        setLoading(false);
        return;
      }

      // Validar campos obrigatórios
      const errors: string[] = [];
      jsonData.forEach((row, index) => {
        if (!row.nome) errors.push(`Linha ${index + 2}: Nome é obrigatório`);
        if (!row.email) errors.push(`Linha ${index + 2}: Email é obrigatório`);
        if (!row.usuario) errors.push(`Linha ${index + 2}: Usuário é obrigatório`);
        if (!row.cpf_cnpj) errors.push(`Linha ${index + 2}: CPF/CNPJ é obrigatório`);
      });

      if (errors.length > 0) {
        notify(`Erros na planilha:\n${errors.slice(0, 5).join('\n')}`, { type: 'error' });
        setLoading(false);
        return;
      }

      // Processar e enviar dados
      const usersData = jsonData.map(parseUserData);

      // Log dos dados para debug
      console.log('Dados a serem enviados:', JSON.stringify(usersData, null, 2));

      // Enviar para o backend
      await API.post('/admin/users/import', { users: usersData });

      notify(`${usersData.length} usuário(s) importado(s) com sucesso`, { type: 'success' });
      setOpen(false);
      setFile(null);
      refresh();
    } catch (error: any) {
      console.error('Erro ao importar usuários:', error);
      console.error('Detalhes do erro:', error?.response?.data);
      
      // Mensagem de erro mais detalhada
      const errorMessage = error?.response?.data?.message 
        || error?.response?.data?.error
        || (error?.response?.data?.errors ? JSON.stringify(error.response.data.errors) : null)
        || 'Erro ao importar usuários. Verifique o console para mais detalhes.';
      
      notify(errorMessage, { type: 'error', autoHideDuration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={() => setOpen(true)}
      >
        <UploadIcon style={{ marginRight: 8 }} />
        Importar
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar Usuários</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>Formato do arquivo:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • Arquivo Excel (.xlsx)<br />
                • Colunas obrigatórias: nome, email, usuario, cpf_cnpj<br />
                • Colunas opcionais: telefone, emails_secundarios, telefones, cep, logradouro, numero, complemento, bairro, cidade, estado, empresa_id, observacoes
              </Typography>
              <Box mt={1}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={downloadTemplate}
                  style={{ cursor: 'pointer' }}
                >
                  Baixar planilha modelo
                </Link>
              </Box>
            </Alert>
          </Box>

          <input
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            id="import-file"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="import-file">
            <Button variant="outlined" component="span" fullWidth disabled={loading}>
              {file ? file.name : 'Selecionar arquivo'}
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            disabled={!file || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
